import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./ResetPassword.css";

import { EyeOffIcon, EyeIcon, Mail, Loader2 } from "lucide-react";

import Logo from "../shared/Logo";

import {
  validateResetPasswordLink,
  resetPasswordFromEmailApi,
  forgotPasswordApi,
} from "../../services/authService";
import { Lock } from "lucide-react";
import MessageManager from "../shared/Message";
import FeedbackModal from "../shared/modals/FeedbackModal";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  // add these states near your other useState calls
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({ new: false, confirm: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [checkingLink, setCheckingLink] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const openForgot = useCallback((prefill = "") => {
    setForgotEmail(prefill || "");
    setForgotError("");
    setIsForgotOpen(true);
  }, []);

  const closeForgot = useCallback(() => {
    setIsForgotOpen(false);
    setForgotSending(false);
    setForgotError("");
  }, []);

  useEffect(() => {
    if (!isForgotOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && closeForgot();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isForgotOpen, closeForgot]);

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!isValidEmail(forgotEmail)) {
      setForgotError("Enter a valid email address.");
      return;
    }
    try {
      setForgotSending(true);
      const response = await forgotPasswordApi({ email: forgotEmail });
      if (response.code === 200 || response.status === 200) {
        MessageManager.showToast("success", "Password reset link sent");
        closeForgot();
      }
    } catch (err) {
      setForgotError("Something went wrong. Please try again.");
    } finally {
      setForgotSending(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        navigate("/signin", { replace: true });
        return;
      }

      setCheckingLink(true);
      setStatus({ type: "", message: "" });

      try {
        await validateResetPasswordLink(token);

        if (cancelled) return;
        setLinkValid(true);
      } catch (e) {
        if (cancelled) return;
        setLinkValid(false);

        // Extract error message from API response
        const errorMessage = e?.response?.data?.message || e?.message || "";

        const lowerError = errorMessage.toLowerCase();

        // Determine banner message based on error type
        let bannerMessage =
          "This reset link is invalid. Please request a new password reset.";

        if (lowerError.includes("already been used")) {
          bannerMessage =
            "This reset link has already been used. Please request a new password reset.";
        } else if (lowerError.includes("expired")) {
          bannerMessage =
            "This reset link has expired. Please request a new password reset.";
        }

        const bannerId = MessageManager.showBanner("error", bannerMessage, [
          {
            label: "Request new link",
            type: "primary",
            onClick: () => {
              MessageManager.removeBanner(bannerId);
              openForgot();
            },
          },
          {
            label: "Dismiss",
            type: "secondary",
            onClick: () => {
              MessageManager.removeBanner(bannerId);
            },
          },
        ]);
      } finally {
        if (!cancelled) setCheckingLink(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  // const validate = () => {
  //   if (!newPassword || !confirmPassword) return "Please fill in both fields."
  //   if (newPassword.length < 8) return "Password must be at least 8 characters."
  //   if (newPassword !== confirmPassword) return "Passwords do not match."
  //   return ""
  // }

  const validateNewPassword = (pwd) => {
    const errors = [];

    if (!pwd) return errors; // don't show anything when empty (optional)

    if (pwd.length < 8)
      errors.push("Password must be at least 8 characters long");
    if (!/[a-z]/.test(pwd))
      errors.push("Password must contain at least one lowercase letter (a-z)");
    if (!/[A-Z]/.test(pwd))
      errors.push("Password must contain at least one uppercase letter (A-Z)");
    if (!/[0-9]/.test(pwd))
      errors.push("Password must contain at least one number (0-9)");
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd))
      errors.push("Password must contain at least one special character");
    if (/(.)\1{2,}/.test(pwd))
      errors.push(
        "Password cannot contain 3 or more consecutive identical characters",
      );

    return errors;
  };

  const validateConfirmPassword = (pwd, confirm) => {
    const errors = [];
    if (!confirm) return errors; // optional
    if (pwd !== confirm) errors.push("Passwords do not match");
    return errors;
  };
  const newPwdErrors = validateNewPassword(newPassword);
  const confirmPwdErrors = validateConfirmPassword(
    newPassword,
    confirmPassword,
  );

  const showNewPwdErrors =
    (touched.new || submitAttempted) && newPwdErrors.length > 0;
  const showConfirmErrors =
    (touched.confirm || submitAttempted) && confirmPwdErrors.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setStatus({ type: "", message: "" });

    const newErrors = validateNewPassword(newPassword);
    const confErrors = validateConfirmPassword(newPassword, confirmPassword);

    if (newErrors.length > 0 || confErrors.length > 0) return;

    setLoading(true);
    try {
      const obj = { token, resetPassword: confirmPassword };
      const response = await resetPasswordFromEmailApi(obj);

      // setStatus({ type: "success", message: "Password reset successful. Redirecting to signin..." })
      if (response.code === 200) {
        MessageManager.showToast(
          "success",
          `Password updated. Redirecting to signin...`,
        );
        setTimeout(() => {
          navigate("/signin", { replace: true });
        }, 1500);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message:
          "Unable to reset password. Please check the password requirements and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="rp-page">
        <div className="Header-with-logo">
          <Logo width={80} height={40} className="logo-svg" />
        </div>
        <main className="rp-main">
          <div className="rp-card">
            {checkingLink && (
              <div className="rp-alert">Validating reset link...</div>
            )}

            {status.message && !checkingLink && (
              <div
                className={`rp-alert ${
                  status.type === "success"
                    ? "rp-alert-success"
                    : "rp-alert-error"
                }`}
              >
                {status.message}
              </div>
            )}

            {!checkingLink && (
              <form
                className="rp-form"
                onSubmit={handleSubmit}
                disabled={!linkValid}
              >
                <div className="rp-form-header">
                  <div className="forgot-icon-bg">
                    <Lock size={38} className="forgot-icon" color="#505050" />
                  </div>
                  <h1>Reset password</h1>
                  <p>Choose a strong new password and submit it below.</p>
                </div>
                <label className="rp-label">
                  <span>
                    New Password<span style={{ color: "red" }}>*</span>
                  </span>
                  <div className="rp-input-wrap">
                    <input
                      className={`rp-input ${
                        showNewPwdErrors ? "rp-input-error" : ""
                      }`}
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, new: true }))}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      aria-invalid={showNewPwdErrors}
                      disabled={!linkValid}
                    />

                    <button
                      type="button"
                      className="rp-eye-btn"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={
                        showNewPassword ? "Hide password" : "Show password"
                      }
                      disabled={!linkValid}
                    >
                      {showNewPassword ? (
                        <EyeOffIcon size={18} />
                      ) : (
                        <EyeIcon size={18} />
                      )}
                    </button>
                  </div>
                  {showNewPwdErrors && (
                    <ul className="rp-inline-errors">
                      {newPwdErrors.map((msg) => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </label>

                <label className="rp-label">
                  <span>
                    Confirm Password<span style={{ color: "red" }}>*</span>
                  </span>
                  <div className="rp-input-wrap">
                    <input
                      className={`rp-input ${
                        showConfirmErrors ? "rp-input-error" : ""
                      }`}
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, confirm: true }))
                      }
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      aria-invalid={showConfirmErrors}
                      disabled={!linkValid}
                    />

                    <button
                      type="button"
                      className="rp-eye-btn"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      disabled={!linkValid}
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon size={18} />
                      ) : (
                        <EyeIcon size={18} />
                      )}
                    </button>
                  </div>
                  {showConfirmErrors && (
                    <ul className="rp-inline-errors">
                      {confirmPwdErrors.map((msg) => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </label>

                <div className="rp-btn-container">
                  <button
                    className="rp-submit"
                    type="submit"
                    disabled={
                      !linkValid || loading || !newPassword || !confirmPassword
                    }
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>

      <FeedbackModal
        show={isForgotOpen}
        onClose={closeForgot}
        onConfirm={submitForgot}
        type="info"
        title="Request a new password reset link"
        message="Enter the email associated with your account. We'll send you a new reset link."
        showButton={false}
      >
        <form onSubmit={submitForgot} style={{ marginTop: "1rem" }}>
          <div className="auth-field">
            <label htmlFor="forgot-email" className="auth-label">
              <Mail size={14} />
              <span>
                Email Address <span style={{ color: "red" }}>*</span>
              </span>
            </label>

            <input
              id="forgot-email"
              type="email"
              className="auth-input"
              placeholder="you@company.com"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                setForgotError("");
              }}
              required
              autoFocus
            />

            {forgotError && (
              <p
                className="input-field-error-message"
                style={{ color: "#dc2626", marginTop: 8, fontSize: "12px" }}
              >
                {forgotError}
              </p>
            )}
          </div>

          <div
            className="modal-footer"
            style={{
              display: "flex",
              gap: 8,
              marginTop: 24,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeForgot}
              disabled={forgotSending}
              style={{
                border: "1px solid #d0d5dd",
                padding: "10px 20px",
                borderRadius: "8px",
                background: "white",
                color: "#000",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={forgotSending || !isValidEmail(forgotEmail)}
              style={{
                borderRadius: "8px",
                padding: "10px 20px",
                background: "#0e1822",
                color: "white",
                border: "none",
                cursor:
                  forgotSending || !isValidEmail(forgotEmail)
                    ? "not-allowed"
                    : "pointer",
                opacity: forgotSending || !isValidEmail(forgotEmail) ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {forgotSending && <Loader2 className="spin" size={16} />}
              {forgotSending ? "Sending..." : "Send reset link"}
            </button>
          </div>
        </form>
      </FeedbackModal>
    </>
  );
}
